import React from "react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { capitalizeFirstLetter } from "@/lib/utils";
import { EMAIL_ACCESS_RULES } from "@/lib/constants";

// Permissions shape used by the page
export type FeaturePermissions = {
  view: boolean;
  modify: boolean;
  add: boolean;
  delete: boolean;
};

export type Features = Record<string, FeaturePermissions>;

const ADMIN_FEATURES: Features = {
  Products: { view: true, modify: true, add: true, delete: true },
  Categories: { view: true, modify: true, add: true, delete: true },
  Feeds: { view: true, modify: true, add: true, delete: true },
  Notifications: { view: true, modify: true, add: true, delete: true },
};

function buildFeaturesFromAllowed(allowedMenuIds: string[]): Features {
  const allowFeed = allowedMenuIds.includes("feed");
  const allowNotifications = allowedMenuIds.includes("notifications");
  return {
    Products: { view: false, modify: false, add: false, delete: false },
    Categories: { view: false, modify: false, add: false, delete: false },
    Feeds: {
      view: allowFeed,
      modify: allowFeed,
      add: allowFeed,
      delete: false, // Remove delete access for Feed Manager
    },
    Notifications: {
      view: allowNotifications,
      modify: allowNotifications,
      add: allowNotifications,
      delete: false, // Remove delete access for Notification Manager
    },
  };
}

const ROLE_TO_ALLOWED: Record<string, string[]> = {
  admin: ["feed", "notifications", "products", "categories"],
  "feed manager": ["feed"],
  "notification manager": ["notifications"],
};

export default function StaffPermission() {
  const [currentRole, setCurrentRole] = React.useState<string>("admin");
  const [features, setFeatures] = React.useState<Features>(ADMIN_FEATURES);

  React.useEffect(() => {
    if (currentRole === "admin") {
      setFeatures(ADMIN_FEATURES);
      return;
    }
    const allowed = ROLE_TO_ALLOWED[currentRole];
    if (allowed) {
      setFeatures(buildFeaturesFromAllowed(allowed));
      return;
    }
    // Fallback: if a literal email gets passed as role somehow
    const rule = EMAIL_ACCESS_RULES[currentRole as keyof typeof EMAIL_ACCESS_RULES];
    if (rule) setFeatures(buildFeaturesFromAllowed(rule.allowedMenuIds));
  }, [currentRole]);

  return (
    <div className="space-y-5">
      <h1 className="text-2xl">Staff Permissions</h1>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl min-w-max">
          {capitalizeFirstLetter(currentRole, true)}
        </h2>
        <Select value={currentRole} onValueChange={setCurrentRole}>
          <SelectTrigger className="min-w-32">
            <SelectValue placeholder="Select User Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="feed manager">Feed Manager</SelectItem>
            <SelectItem value="notification manager">
              Notification Manager
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {Object.keys(features).length > 0 && (
        <Table className="text-base">
          <TableCaption>
            A list of staff permissions for the admin panel.
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Feature</TableHead>
              <TableHead className="text-center">View</TableHead>
              <TableHead className="text-center">Modify</TableHead>
              <TableHead className="text-center">Add</TableHead>
              <TableHead className="text-center">Delete</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(features).map(([feature, permissions]) => (
              <TableRow key={feature}>
                <TableCell>{feature}</TableCell>
                <TableCell className="text-center align-middle p-0">
                  <span className="flex items-center justify-center h-10">
                    {permissions.view ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="#22c55e"
                        style={{ width: 20, height: 20 }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 6.29a1 1 0 0 1 0 1.414l-6.25 6.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 5.543-5.543a1 1 0 0 1 1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span
                        style={{
                          color: "#ef4444",
                          fontWeight: "bold",
                          fontSize: "0.7em",
                        }}
                      >
                        ❌
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-center align-middle p-0">
                  <span className="flex items-center justify-center h-10">
                    {permissions.modify ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="#22c55e"
                        style={{ width: 20, height: 20 }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 6.29a1 1 0 0 1 0 1.414l-6.25 6.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 5.543-5.543a1 1 0 0 1 1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span
                        style={{
                          color: "#ef4444",
                          fontWeight: "bold",
                          fontSize: "1.2em",
                        }}
                      >
                        ❌
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-center align-middle p-0">
                  <span className="flex items-center justify-center h-10">
                    {permissions.add ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="#22c55e"
                        style={{ width: 20, height: 20 }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 6.29a1 1 0 0 1 0 1.414l-6.25 6.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 5.543-5.543a1 1 0 0 1 1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span
                        style={{
                          color: "#ef4444",
                          fontWeight: "bold",
                          fontSize: "1.2em",
                        }}
                      >
                        ❌
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-center align-middle p-0">
                  <span className="flex items-center justify-center h-10">
                    {permissions.delete ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="#22c55e"
                        style={{ width: 20, height: 20 }}
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 6.29a1 1 0 0 1 0 1.414l-6.25 6.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414l2.543 2.543 5.543-5.543a1 1 0 0 1 1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span
                        style={{
                          color: "#ef4444",
                          fontWeight: "bold",
                          fontSize: "1.2em",
                        }}
                      >
                        ❌
                      </span>
                    )}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

